import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { brands, isProduction } from "@/lib/comparatifs";

export default function Comparatifs() {
  const canonicalUrl = "https://www.cosmos-wave.com/comparatifs";

  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Comparatifs & alternatives agences Amazon (2025)",
    "description": "Comparaison impartiale des principales agences Amazon : alternatives, spécialisations, méthodes. Guide pour choisir le bon partenaire Amazon Ads & SEO.",
    "url": canonicalUrl,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": brands.map((brand, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Organization",
          "name": brand.name,
          "url": brand.homepage
        }
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.cosmos-wave.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Comparatifs",
        "item": canonicalUrl
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Comparatifs & alternatives agences Amazon (2025) | Cosmos Wave</title>
        <meta name="description" content="Comparaison impartiale des principales agences Amazon : alternatives, spécialisations, méthodes. Guide pour choisir le bon partenaire Amazon Ads & SEO." />
        <link rel="canonical" href={canonicalUrl} />
        {!isProduction() && <meta name="robots" content="noindex,nofollow" />}
        
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cosmos Wave" />
        <meta property="og:title" content="Comparatifs & alternatives agences Amazon (2025) | Cosmos Wave" />
        <meta property="og:description" content="Comparaison impartiale des principales agences Amazon : alternatives, spécialisations, méthodes. Guide pour choisir le bon partenaire Amazon Ads & SEO." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.cosmos-wave.com/og/comparatifs-hub-1200x630.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Comparatifs & alternatives agences Amazon (2025) | Cosmos Wave" />
        <meta name="twitter:description" content="Comparaison impartiale des principales agences Amazon : alternatives, spécialisations, méthodes. Guide pour choisir le bon partenaire Amazon Ads & SEO." />
        <meta name="twitter:image" content="https://www.cosmos-wave.com/og/comparatifs-hub-1200x630.jpg" />
        
        <script type="application/ld+json">
          {JSON.stringify(collectionPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Comparatifs & Alternatives
                <span className="block text-primary mt-2">Agences Amazon (2025)</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Comparaison impartiale des principales agences Amazon : alternatives, spécialisations, méthodes. 
                Guide pour choisir le bon partenaire Amazon Ads & SEO.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-8 text-foreground">Guide Complet</h2>
              <div className="bg-card rounded-lg p-6 border border-border">
                <Link 
                  to="/comparatifs/top-alternatives-agences-amazon" 
                  className="block hover:bg-accent/50 p-4 rounded-lg transition-colors"
                >
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Meilleures alternatives agences Amazon (2025)
                  </h3>
                  <p className="text-muted-foreground">
                    Critères de choix, erreurs à éviter, checklist téléchargeable pour sélectionner votre agence Amazon.
                  </p>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-8 text-foreground">Alternatives par Agence</h2>
              <div className="grid gap-4">
                {brands.map((brand) => (
                  <div key={brand.slug} className="bg-card rounded-lg p-6 border border-border">
                    <Link 
                      to={`/comparatifs/alternatives/${brand.slug}`}
                      className="block hover:bg-accent/50 p-4 -m-4 rounded-lg transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Alternatives à {brand.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {brand.focus.slice(0, 3).map((focus) => (
                          <span key={focus} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                            {focus}
                          </span>
                        ))}
                      </div>
                      <p className="text-muted-foreground">
                        Comparaison neutre avec 4 alternatives incluant profils spécialisés et critères détaillés.
                      </p>
                    </Link>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-8 text-foreground">Comparaisons Directes</h2>
              <div className="grid gap-4">
                {brands.map((brand) => (
                  <div key={`vs-${brand.slug}`} className="bg-card rounded-lg p-6 border border-border">
                    <Link 
                      to={`/comparatifs/${brand.slug}-vs-cosmos-wave`}
                      className="block hover:bg-accent/50 p-4 -m-4 rounded-lg transition-colors"
                    >
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        {brand.name} vs Cosmos Wave
                      </h3>
                      <p className="text-muted-foreground">
                        Comparaison directe des méthodes, stack IA, cas d'usage et délais de mise en œuvre.
                      </p>
                    </Link>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16 text-center">
              <div className="bg-primary/5 rounded-lg p-8">
                <h2 className="text-2xl font-semibold mb-4 text-foreground">
                  Évaluation Gratuite en 48-72h
                </h2>
                <p className="text-muted-foreground mb-6">
                  Diagnostic IA 360° de votre présence Amazon avec plan d'action priorisé.
                </p>
                <Link 
                  to="/ia/audit" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Demander un audit gratuit
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}